import React, { useEffect } from 'react';
import { DatePicker, Form, Modal, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
import type { HostTenantListItem } from '../../tenants/api/tenants';

const { Text } = Typography;

interface EditTenantLicenseModalProps {
  tenant: HostTenantListItem | null;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (tenantId: string, licenseEndsAtUtc: string) => void;
}

interface FormValues {
  licenseEndsAt: Dayjs;
}

const EditTenantLicenseModal: React.FC<EditTenantLicenseModalProps> = ({
  tenant,
  open,
  saving,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (open && tenant?.licenseEndsAtUtc) {
      form.setFieldsValue({ licenseEndsAt: dayjs(tenant.licenseEndsAtUtc) });
    }
  }, [open, tenant, form]);

  function handleClose() {
    form.resetFields();
    onClose();
  }

  function handleFinish(values: FormValues) {
    if (!tenant) {
      return;
    }

    onSave(tenant.id, values.licenseEndsAt.utc().startOf('day').toISOString());
  }

  return (
    <Modal
      title="Update license"
      open={open}
      onCancel={handleClose}
      onOk={() => form.submit()}
      okText="Save"
      confirmLoading={saving}
      destroyOnClose
    >
      {tenant && (
        <Text style={{ display: 'block', marginBottom: 16 }}>
          Set license end date for <strong>{tenant.companyName}</strong>.
        </Text>
      )}
      <div className="cp-form-section">
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="licenseEndsAt"
            label="License end date"
            rules={[{ required: true, message: 'Select a license end date' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={(current) => current !== null && current < dayjs().startOf('day')}
              getPopupContainer={(trigger) => trigger.parentElement!}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default EditTenantLicenseModal;
